import _ from 'lodash';
import deepdash from 'deepdash-es';

import { useCallback, useEffect, useState } from 'react';
import { checkImageIsBase64, copyUpdatedData, matchObjects } from '../utils/utilities';

import { getReportDetails } from '../store/reportDetails';
import { AUTOSAVE_INTERVAL } from '../utils/constants';
import { base64Upload } from '../services/fileUpload';

import { getReportCacheProgress } from '../services/reportCache';
import { useAppSelector } from '../store/configureStore';

import useDataCache, { CachePayload } from './useDataCache';
import config from '../utils/config';
import logger from '../services/logService';

interface ReportValidationParams {
	updatedReportData?: any;
	baseReportData: any;
}

const useAutoSaveReport = (reportToSave: any) => {
	const [oldReportData, setOldReportData] = useState<any>(null);
	const [lastSaved, setLastSaved] = useState<number | null>(null);
	const [isProcessing, setProcessing] = useState(false);

	const projectId: string = reportToSave.projectDetails._id;

	const { data, loading, onUpdateData, storeDataInCache } = useDataCache(projectId);
	const { isSaving } = useAppSelector(getReportDetails);

	const mapBase64ImagesToUrl = useCallback(async (data: object) => {
		const dataToSave = { ...data };
		const pathsToTraverse = deepdash(_).paths(dataToSave).filter((path) => path.indexOf('src') !== -1);

		for (const path of pathsToTraverse) {
			const value = _.get(dataToSave, path);

			if (value && checkImageIsBase64(value) && navigator.onLine) {
				const image = await base64Upload(new Date().getTime().toString(), value);
				if (image) _.set(dataToSave, path, image);
			}
		}

		return dataToSave;
	}, []);

	const validateInspectionItems = useCallback((params: ReportValidationParams) => {
		const { projectDetails } = reportToSave;
		const { baseReportData, updatedReportData } = params;

		const itemTypes = {
			[config.supportedTemplates.exxonMobilFVIPiping]: ['pipingItems'],
			[config.supportedTemplates.exxonMobilFVIEquipment]: ['equipmentItems'],
			[config.supportedTemplates.pcnlStructuralIntegrityReport]: ['pcnlItems'],
			[config.supportedTemplates.exxonMobilMPI]: ['mpiReport.equipment', 'photoRecord', 'drawing'],
			[config.supportedTemplates.exxonMobilPEC]: ['technologyIntroduction', 'fviData.anomalyItems', 'generalInfo.maxwellEquipmentInformation.model', 'summaryData.executiveSummary', 'pecScans', 'scanPlan', 'photoRecord', 'generalDrawing'],
		};

		const dataKey = itemTypes[projectDetails.inspectionTypeId];
		if (!dataKey || dataKey.length === 0) return true;

		const isUpdatedDataValid = updatedReportData ? dataKey.some((key) => _.get(updatedReportData, key).length > 0) : true;
		return isUpdatedDataValid && dataKey.some((key) => _.get(baseReportData, key).length > 0);
	}, [reportToSave]);

	const handleUpdateData = useCallback(async (data: CachePayload) => {
			if (!navigator.onLine) {
				storeDataInCache(data);
			}
	
			await onUpdateData(data);
			setLastSaved(Date.now());
	}, [onUpdateData, storeDataInCache]);

	const saveReport = useCallback(async () => {
		const isNotFocused = !document.hasFocus();
		if (isNotFocused || isSaving || loading) {
			return;
		}
		
		setProcessing(true);

		try {
			const result = await getReportCacheProgress(projectId);
			if (result.data) {
				const isMatch: boolean = matchObjects(reportToSave, result.data.value);
				if (isMatch) {
					return;
				}

				const [updatedReport, latestChange] = copyUpdatedData(result.data.value, reportToSave);
				if (validateInspectionItems({ baseReportData: reportToSave, updatedReportData: updatedReport })) {
					const updated = await mapBase64ImagesToUrl(updatedReport);
					await handleUpdateData({ updated, latestChange });
				}
			} else {
				const updated = await mapBase64ImagesToUrl(reportToSave);
				if (validateInspectionItems({ baseReportData: updated })) {
					await handleUpdateData({ updated, latestChange: '' });
				}
			}
		} catch (error) {
			logger.log(error as Error);
		} finally {
			setProcessing(false);
		}
	}, [projectId, reportToSave, validateInspectionItems, handleUpdateData, mapBase64ImagesToUrl, isSaving, loading]);

	useEffect(() => {
		const interval = setInterval(saveReport, AUTOSAVE_INTERVAL);

		return () => clearInterval(interval);
	}, [saveReport]);

	useEffect(() => {
		if (data && data.data) {
			setOldReportData(data.data.value);
		}

	}, [data]);

	return { oldReportData, saveReport, lastSaved, isProcessing };
};

export default useAutoSaveReport;
